import React, {lazy, Suspense, useState} from "react";
import {Route, Routes} from "react-router-dom";
import {RedirectToLink} from "../utils/Redirect.js";
import ProtectedRoute from "../utils/ProtectedRoute.jsx";
import useScrollToTop from "../utils/ScrollToTop.js";
import LinearLoadingBar from "../components/Loader";
import MfaInput from "../components/MfaInput.jsx";
import {useSelector} from "react-redux";
import * as Sentry from "@sentry/browser";
// import FilterBuyersBulkSms from "./bulksms/FilterBuyersBulkSms.jsx";
// import HandleShortcutReturn from "./bulksms/HandleShortcutReturn.jsx";

// import TargetSetup from "../components/1on1Partials/TargetSetup.jsx";

const Login = lazy(() => import("./Login.jsx"));
const Todo = lazy(() => import("./todo/Todo.jsx"));
const Home = lazy(() => import("./menu/Menu.jsx"));
const Diary = lazy(() => import("./diary/Diary.jsx"));
const ActivityLog = lazy(() => import("./ActivityLog/ActivityLog.jsx"));
const EmployeeNotes = lazy(() => import("./EmployeeNotes/EmployeeNotes.jsx"));
const OpenHome = lazy(() => import("./OpenHomes/openHomes.jsx"));
const HomeOpen = lazy(() => import("./OpenHomes/Home-Open.jsx"));
const OneOnOne = lazy(() => import("./1on1/OneonOne.jsx"));
const Enquiry = lazy(() => import("./enquiriesLog/EnquiriesLog.jsx"));
const Listing = lazy(() => import("./listing/Listing.jsx"));
const Prospect = lazy(() => import("./prospect/Prospect.jsx"));
const CustomNotes = lazy(() => import("./CustomNotes/CustomNotes.jsx"));
const BulkSms = lazy(() => import("./bulksms/BulkSms.jsx"));
const BulkSmsHome = lazy(() => import("./bulksms/BulkSmsHome.jsx"));
const GeoBulkSms = lazy(() => import("./bulksms/GeoBulkSms.jsx"));
const BulkSmsStatusPage = lazy(() => import("./bulksms/BulkSmsStatusPage.jsx"));
const MyStreet = lazy(() => import("./bulksms/MyStreet.jsx"));
const StreetDetail = lazy(() => import("./bulksms/StreetDetail.jsx"));
const FilterBuyersBulkSms = lazy(() =>
  import("./bulksms/FilterBuyersBulkSms.jsx")
);
const HandleShortcutReturn = lazy(() =>
  import("./bulksms/HandleShortcutReturn.jsx")
);

// --- Add lazy import for the new component ---
const ProspectFilterSms = lazy(() => import("./bulksms/ProspectFilterSms.jsx"));
const HOListBulkSms = lazy(() => import("./bulksms/HOListBulkSms.jsx"));
// ------------------------------------------

const ProspectDetails = lazy(() => import("./prospect/ProspectDetail.jsx"));
const BuyerDetails = lazy(() => import("./Buyers/BuyerDetails.jsx"));
const ListingDetails = lazy(() => import("./listing/ListingDetails.jsx"));
const Buyers = lazy(() => import("./Buyers/Buyers.jsx"));
const MatchedBuyers = lazy(() =>
  import("../components/ListingPartials/MatchedBuyers.jsx")
);
const TargetSetup = lazy(() =>
  import("../components/1on1Partials/TargetSetup.jsx")
);
const RouteManager = ({ dataTestId }) => {
  useScrollToTop();
  const sessionUser = useSelector((state) => state.auth.sessionUser);
  Sentry.setUser({
    username: `${sessionUser?.contact?.first_name} ${sessionUser?.contact?.last_name} || ${sessionUser?.organisation?.name}`,
    email:
      sessionUser?.contact?.emails[0]?.value || "error getting email address",
    id: sessionUser?.id,
  });
  return (
    <div data-testid={dataTestId}>
      <Suspense
        fallback={
          <div className='w-full  mt-2'>
            <LinearLoadingBar />
          </div>
        }>
        <Routes>
          <Route path='/' element={<Login />} />
          <Route path='/mfa' element={<MfaInput />} />
          <Route
            path='/dashboard'
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path='/employee-notes'
            element={
              <ProtectedRoute>
                <EmployeeNotes />
              </ProtectedRoute>
            }
          />
          <Route
            path='/custom-notes'
            element={
              <ProtectedRoute>
                <CustomNotes />
              </ProtectedRoute>
            }
          />
          <Route
            path='/buyers'
            element={
              <ProtectedRoute>
                <Buyers />
              </ProtectedRoute>
            }
          />
          <Route
            path='/open-homes'
            element={
              <ProtectedRoute>
                <HomeOpen />
              </ProtectedRoute>
            }
          />
          <Route
            path='/open-homes/:id'
            element={
              <ProtectedRoute>
                <OpenHome />
              </ProtectedRoute>
            }
          />
          <Route
            path='/1-on-1'
            element={
              <ProtectedRoute>
                <OneOnOne />
              </ProtectedRoute>
            }
          />
          <Route
            path='/enquiry-log'
            element={
              <ProtectedRoute>
                <Enquiry />
              </ProtectedRoute>
            }
          />
          <Route
            path='/listing'
            element={
              <ProtectedRoute>
                <Listing />
              </ProtectedRoute>
            }
          />
          <Route
            path='/matched-buyers/:id'
            element={
              <ProtectedRoute>
                <MatchedBuyers />
              </ProtectedRoute>
            }
          />
          <Route
            path='/prospect'
            element={
              <ProtectedRoute>
                <Prospect />
              </ProtectedRoute>
            }
          />
          <Route
            path='/prospect-detail'
            element={
              <ProtectedRoute>
                <ProspectDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path='/buyer-detail/:id'
            element={
              <ProtectedRoute>
                <BuyerDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path='/listing/:id'
            element={
              <ProtectedRoute>
                <ListingDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path='/to-do'
            element={
              <ProtectedRoute>
                <Todo />
              </ProtectedRoute>
            }
          />
          <Route
            path='/diary'
            element={
              <ProtectedRoute>
                <Diary />
              </ProtectedRoute>
            }
          />
          <Route
            path='/target-setup'
            element={
              <ProtectedRoute>
                <TargetSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path='/activity-log'
            element={
              <ProtectedRoute>
                <ActivityLog />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms'
            element={
              <ProtectedRoute>
                <BulkSmsHome />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/send'
            element={
              <ProtectedRoute>
                <BulkSms />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/geo'
            element={
              <ProtectedRoute>
                <GeoBulkSms />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/my-street'
            element={
              <ProtectedRoute>
                <MyStreet />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/my-street/prospects'
            element={
              <ProtectedRoute>
                <StreetDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/filter-buyers'
            element={
              <ProtectedRoute>
                <FilterBuyersBulkSms />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/handle-return'
            element={
              <ProtectedRoute>
                <HandleShortcutReturn />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/status/:jobId'
            element={
              <ProtectedRoute>
                <BulkSmsStatusPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/prospect'
            element={
              <ProtectedRoute>
                <ProspectFilterSms />
              </ProtectedRoute>
            }
          />
          <Route
            path='/bulk-sms/ho-list'
            element={
              <ProtectedRoute>
                <HOListBulkSms />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
};

export default RouteManager;
